// 週末・祝日帯などのアノテーションをチャートの背面に移動する。
//
// ApexCharts は annotations.position: "back" を実装しておらず（v4/v5/v6共通）、
// アノテーショングループ（.apexcharts-xaxis-annotations 等の3グループ）を
// .apexcharts-graphical の末尾に追加するため常に最前面に描画される。
// SVG要素に z-index は効かないため、DOM順を並べ替えて背面化する。
export function moveAnnotationsToBack(rootEl) {
  if (!rootEl) {
    return
  }

  const reposition = () => {
    // 注意: apexcharts-inner と apexcharts-graphical は同一要素に付与される
    // （子孫セレクタ ".apexcharts-inner .apexcharts-graphical" では一致しない）
    const graphical = rootEl.querySelector(".apexcharts-graphical")
    if (!graphical) {
      return
    }

    const annotationGroups = graphical.querySelectorAll(
      ".apexcharts-xaxis-annotations, .apexcharts-yaxis-annotations, .apexcharts-point-annotations"
    )

    // SVGの描画順序：最初の子要素が最背面になる
    annotationGroups.forEach(group => {
      if (graphical.firstChild !== group) {
        graphical.insertBefore(group, graphical.firstChild)
      }
      group.style.pointerEvents = "none"
      group.querySelectorAll("rect").forEach(rect => {
        rect.style.pointerEvents = "none"
      })
    })
  }

  reposition()
  // 念のため、少し遅延して再実行（ApexChartsの内部レンダリング完了を待つ）
  setTimeout(reposition, 50)
}

// x 軸注釈のラベル同士の最小間隔(px)。背景 rect には左右 5px の余白があるので、その外側にさらに空ける幅
export const XAXIS_ANNOTATION_LABEL_GAP = 4

// 境界の比較で丸め誤差を許す幅(px)。getBBox は小数を返すため、端にぴったり置いたラベルを落とさないようにする
const BOUNDS_EPSILON = 0.5

// 表示するラベルの index(昇順)を返す純関数。
// - boxes: DOM 順(= 日付の昇順)に並んだ { x, width, contentX?, contentWidth? }。
//   x / width は重なりの判定に使う範囲(背景 rect)、contentX / contentWidth は境界の判定に使う範囲(文字)。
//   contentX が無ければ x / width で境界を判定する
// - gap: ラベル同士がこの px 以上離れていなければ重なりとみなす
// - left / right: 境界(チャートのローカル座標)。はみ出すものは先に除き、ほかのラベルの判定には使わない
// 新しい変更ほど今の線の動きの説明に要るので、後ろ(新しい方)から選ぶ。x の並びと DOM の並びは
// アンカーの違いで逆転しうるため、隣だけでなく残したもの全部と区間の交差で判定する。
// 幅 0 のもの(描画前など)は場所を取らないので、隠さず、ほかのラベルの判定にも使わない
export function pickVisibleLabelIndexes(boxes, { gap = 0, left = -Infinity, right = Infinity } = {}) {
  if (!Array.isArray(boxes)) return []

  const inBounds = (box) => {
    const x = Number.isFinite(box?.contentX) ? box.contentX : box?.x
    const width = Number.isFinite(box?.contentWidth) ? box.contentWidth : box?.width
    if (!Number.isFinite(x) || !Number.isFinite(width)) return false
    return x >= left - BOUNDS_EPSILON && x + width <= right + BOUNDS_EPSILON
  }
  const candidates = boxes.map((box, index) => ({ box, index })).filter(({ box }) => inBounds(box))

  const kept = []
  const visible = []
  for (let i = candidates.length - 1; i >= 0; i--) {
    const { box, index } = candidates[i]
    if (!(box.width > 0)) {
      visible.push(index)
      continue
    }
    const overlaps = kept.some((other) => box.x < other.x + other.width + gap && other.x < box.x + box.width + gap)
    if (overlaps) continue

    kept.push(box)
    visible.push(index)
  }
  return visible.sort((a, b) => a - b)
}

// x 軸注釈のラベル(text)と背景 rect の組を DOM 順に集める(apexcharts 7.9.1 の DOM 構造)。
// - ラベル: g.apexcharts-xaxis-annotations > text.apexcharts-xaxis-annotation-label
// - 背景: class 無しの rect で、text の直前の兄弟に入る(Helpers#annotationsBackground)。
//   帯の注釈(x2 あり)の rect.apexcharts-annotation-rect や、線(line / path)は背景ではない
// - 文字が空のもの(スマホ幅の mobile_hide_xaxis_annotation_labels など)は背景も作られないので除く
export function collectXaxisAnnotationLabels(rootEl) {
  if (!rootEl) return []

  const texts = rootEl.querySelectorAll(".apexcharts-xaxis-annotations .apexcharts-xaxis-annotation-label")
  return Array.from(texts)
    .filter((text) => (text.textContent || "").trim() !== "")
    .map((text) => {
      const previous = text.previousElementSibling
      const isBackground = previous &&
        String(previous.tagName).toLowerCase() === "rect" &&
        !previous.classList?.contains("apexcharts-annotation-rect")
      return { text, background: isBackground ? previous : null }
    })
}

// 重なった x 軸注釈のラベルを隠す(破線は残す)。新しい方(DOM 順の後ろ)を残す。
// chart: ApexCharts のインスタンス(または chart.w)。境界をレイアウト値から出す。
// 判定はすべてローカル座標(注釈グループは .apexcharts-graphical の中で、grid の左上が原点)で行う。
// - ラベルの範囲: 背景 rect の getBBox()、無ければ text の getBBox()。境界の判定は text の getBBox()
// - 境界: 左は grid の左端(0。y 軸の目盛りラベルと重ねない)、右は SVG の右端
//   (gridWidth + 右余白。右余白 = svgWidth - translateX - gridWidth)
// 非表示の状態(grid や SVG の幅が 0)では何もしない。何度呼んでも同じ結果になるよう、残すラベルは visibility を戻す
export function hideOverlappingXaxisAnnotationLabels(rootEl, chart, { gap = XAXIS_ANNOTATION_LABEL_GAP } = {}) {
  if (!rootEl) return

  const w = chart?.w ?? chart
  const gridWidth = w?.layout?.gridWidth ?? w?.globals?.gridWidth
  const svgWidth = w?.globals?.svgWidth
  const translateX = w?.layout?.translateX ?? w?.globals?.translateX ?? 0
  if (!(gridWidth > 0) || !(svgWidth > 0)) return

  const labels = collectXaxisAnnotationLabels(rootEl)
  if (labels.length === 0) return

  const rightMargin = svgWidth - translateX - gridWidth
  const boxes = labels.map(({ text, background }) => {
    const content = text.getBBox()
    const area = background ? background.getBBox() : content
    return { x: area.x, width: area.width, contentX: content.x, contentWidth: content.width }
  })
  const visible = new Set(pickVisibleLabelIndexes(boxes, { gap, left: 0, right: gridWidth + rightMargin }))

  labels.forEach(({ text, background }, index) => {
    const visibility = visible.has(index) ? "" : "hidden"
    text.style.visibility = visibility
    if (background) background.style.visibility = visibility
  })
}
